import { addDays, format, parseISO, subDays } from "date-fns"
import { useEffect, useState } from "preact/hooks"
import { QueryData, api_fetch } from "@/api"
import { ChartData, MonthlyBreakdownChartBase, RawData } from "./monthly_breakdown_base"
import { toISODateString } from "@/utils"
import { redistributeUncodedAndMisc } from "@/utils/redistribution"
import { useShowRedistribution } from "@/hooks/use_super_coffee"
import { GlobalFilterState } from "@/global_filter"

interface DetailedRawData extends RawData {
  expense_account_group: string
}

const colorMap: { [key: string]: string } = {
  blue: "#3b82f690",
  green: "#22c55e90",
  yellow: "#eab30890",
  red: "#ef444490",
  purple: "#a855f790",
  pink: "#ec489990",
  indigo: "#6366f190",
  teal: "#14b8a690",
  orange: "#f9731690",
  cyan: "#06b6d490",
}

export function MonthlyExpenseAccountGroupExecutionBreakdownChart() {
  const [rawData, setRawData] = useState<DetailedRawData[]>([])
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [redistributedChartData, setRedistributedChartData] = useState<ChartData[]>([])
  const [expenseAccountGroups, setExpenseAccountGroups] = useState<string[]>([])
  const [hasData, setHasData] = useState(false)
  const [isRedistributed, setIsRedistributed] = useState(false)
  const distributor = GlobalFilterState.use(s => s.distributor)

  function handleRedistributeUncodedAndMisc() {
    if (!chartData.length) return
    const redistributed = redistributeUncodedAndMisc(chartData)
    setRedistributedChartData(redistributed)
    setIsRedistributed(true)
  }

  function resetRedistribution() {
    setRedistributedChartData([])
    setIsRedistributed(false)
  }

  useEffect(() => {
    async function fetchData() {
      try {
        const endDate = new Date()
        const startDate = subDays(new Date(), 365)
        let res = await api_fetch<QueryData>(`/deductions/query`, {
          params: {
            query_name: "monthly_expense_account_group_execution_buckets",
            start_date: toISODateString(startDate),
            end_date: toISODateString(endDate),
            distributor,
          },
        })

        if (!res.ok) {
          throw new Error("Failed to fetch data")
        }

        const headerIndex = res.value.data.headers.reduce(
          (acc, header, index) => {
            acc[header] = index
            return acc
          },
          {} as { [key: string]: number }
        )

        const data = res.value.data.rows.map(row => {
          const parsedDate = parseISO(row[headerIndex.month] as string)
          const correctedDate = addDays(parsedDate, 1) // Adjust for off-by-one error
          return {
            month: format(correctedDate, "MMM yyyy"),
            expense_account_group: row[headerIndex.expense_account_group] as string,
            total_amount: Number(row[headerIndex.total_amount]),
          }
        })

        setRawData(data)
        setExpenseAccountGroups([...new Set(data.map(item => item.expense_account_group))])
        // Hide the graph if there's no data or if the only data is "Uncoded"
        const uniqueGroups = [...new Set(data.map(item => item.expense_account_group))]
        setHasData(data.length > 0 && !(uniqueGroups.length === 1 && uniqueGroups[0] === "Uncoded"))
      } catch (error) {
        console.error("Error fetching data:", error)
        setHasData(false)
      }
    }

    fetchData()
  }, [distributor])

  useEffect(() => {
    function processData() {
      const grouped: { [key: string]: ChartData } = {}

      rawData.forEach(item => {
        if (!grouped[item.month]) {
          grouped[item.month] = { month: item.month }
        }
        grouped[item.month][item.expense_account_group] = item.total_amount
      })

      setChartData(Object.values(grouped))
    }

    processData()
  }, [rawData])

  // Reset redistribution when chart data changes
  useEffect(() => {
    resetRedistribution()
  }, [chartData])

  const fallbackColors = Object.keys(colorMap)

  const chartConfig = expenseAccountGroups.reduce((config, group, index) => {
    const colorName = fallbackColors[index % fallbackColors.length]
    const color = colorMap[colorName] || "#64748b90" // Default to a neutral color
    config[group] = {
      label: group,
      color: color,
    }
    return config
  }, {} as any)

  if (!hasData) {
    return null
  }

  const displayData = isRedistributed ? redistributedChartData : chartData
  const hasUncodedOrMisc = expenseAccountGroups.includes("Uncoded") || expenseAccountGroups.includes("Misc")
  const showRedistributeButton = useShowRedistribution(hasUncodedOrMisc)

  return (
    <MonthlyBreakdownChartBase
      title="Monthly Deductions by Group (Execution Date)"
      titleTooltip="Showing total deductions for each expense account group over the last 12 months, grouped by execution date when available"
      chartData={displayData}
      chartConfig={chartConfig}
      dataKeys={expenseAccountGroups}
      downloadFilename="monthly_expense_account_group_execution_breakdown.csv"
      fullWidth={true}
      showRedistributeButton={showRedistributeButton}
      isRedistributed={isRedistributed}
      onRedistribute={handleRedistributeUncodedAndMisc}
      onResetRedistribution={resetRedistribution}
    />
  )
} 